<script lang="ts" setup>
import {useUserStore} from '@/ts/store/user-store';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {computed, ref, watch, onMounted} from 'vue';
import RecapComponent from '@/vue/molecules/components/recap-component.vue';
import SidebarName from '@/vue/molecules/sidebar/sidebar-name.vue';
import {PublicationState, type Intervention, type InterventionPreview} from '@/ts/types/dto/interventions-dto';
import {buildRecapSelectorEntries, type RecapProgramOption, type RecapSelectorEntry} from '@/ts/composables/pure/use-recap-program-options';
import type {ProgramCatalogue} from '@/ts/types/component/today-page';
import {useI18n} from 'vue-i18n';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {InterventionInProgress} from '@/ts/types/dto/home-dto';

const userStore = useUserStore();
const api = useApi();
const {t: $t} = useI18n();

// Get all user interventions
const allInterventions = ref<InterventionPreview[]>([]);

const [fetchAllInterventions, interventionsLoading] = useLoadingNotification(
  async() => api.me.getInterventions(),
  (result: InterventionPreview[]) => {
    allInterventions.value = result;
  },
);

// Filter eligible interventions (IN_PROGRESS or COMPLETED)
const eligibleInterventions = computed<InterventionPreview[]>(() => {
  if (!allInterventions.value) {return [];}
  return allInterventions.value.filter(intervention => 
    [PublicationState.IN_PROGRESS, PublicationState.COMPLETED].includes(intervention.publicationState));
});

// Show intervention selector only if more than one eligible intervention
const showInterventionSelector = computed(() => eligibleInterventions.value.length > 1);

// Grouped under a Balm Programs or Prevention Programs title for each kind of program the user has
const interventionOptions = computed<RecapSelectorEntry[]>(() => buildRecapSelectorEntries(eligibleInterventions.value));

const CATALOGUE_LABEL_KEYS: Record<ProgramCatalogue, string> = {
  medical: 'program_tile_medical_label',
  prevention: 'program_tile_prevention_label',
};

function entryLabel(entry: RecapSelectorEntry): string {
  if (!entry.header) {return entry.label;}
  return entry.catalogue ? $t(CATALOGUE_LABEL_KEYS[entry.catalogue]) : '';
}

function isCatalogueHeader(entry: RecapSelectorEntry): boolean {
  return entry.header;
}

const selectedInterventionId = ref<UuidDTO | null>(null);

onMounted(() => {
  void fetchAllInterventions();
});

// Initialize selected intervention with priority for currentIntervention
watch([
  eligibleInterventions, 
  (): InterventionInProgress | null => userStore.homeContent?.currentIntervention ?? null,
], ([interventions, currentIntervention]): void => {
  if (interventions.length === 0) {return;}
  
  if (currentIntervention) {
    // Since currentIntervention exists, sidebar should be accessible
    // Prioritize currentIntervention if it's in the eligible list
    const currentInList = interventions.find(int => int.id === currentIntervention.interventionId);
    selectedInterventionId.value = currentInList?.id ?? interventions[0].id;
  } else {
    // Fallback: select first intervention (shouldn't happen if currentIntervention logic is correct)
    selectedInterventionId.value = interventions[0].id;
  }
}, {immediate: true});

const selectedInterventionOption = computed({
  get: () => selectedInterventionId.value 
    ? interventionOptions.value.find((opt): opt is RecapProgramOption => !opt.header && opt.value === selectedInterventionId.value) ?? null
    : null,
  set: (option: RecapProgramOption | null) => {
    selectedInterventionId.value = option?.value ?? null;
  },
});

// Get full intervention details for selected intervention
const intervention = ref<Intervention | null>(null);

const [fetchIntervention, fetchInterventionLoading] = useLoadingNotification(
  async(id: UuidDTO) => api.me.getIntervention(id),
  (result: Intervention) => {
    intervention.value = result;
  },
);

watch(selectedInterventionId, (newId): void => {
  if (!newId) {
    intervention.value = null;
    return;
  }
  void fetchIntervention(newId);
}, {immediate: true});
</script>

<template>
  <div>
    <sidebar-name :name="$t('session_recaps')"/>
    <div class="recap">
      <!-- Intervention selector (only show if more than one intervention) -->
      <div v-if="showInterventionSelector" class="intervention-selector">
        <q-select
          v-model="selectedInterventionOption"
          :options="interventionOptions"
          :option-label="entryLabel"
          :option-disable="isCatalogueHeader"
          :label="$t('intervention')"
          class="intervention-select"
        >
          <template #option="scope">
            <q-item-label
              v-if="scope.opt.header"
              header
              class="catalogue-header"
              :class="{
                'catalogue-header--after-group': scope.index > 0,
                'catalogue-header--untitled': !scope.opt.catalogue,
              }"
            >
              {{ entryLabel(scope.opt) }}
            </q-item-label>
            <q-item v-else v-bind="scope.itemProps">
              <q-item-section>{{ scope.opt.label }}</q-item-section>
            </q-item>
          </template>
        </q-select>
      </div>

      <q-skeleton v-if="interventionsLoading || fetchInterventionLoading" class="recap-skeleton-select"/>
      <q-skeleton v-if="interventionsLoading || fetchInterventionLoading" class="recap-skeleton-session"/>

      <div v-else-if="eligibleInterventions.length === 0" class="no-interventions">
        {{ $t('no_eligible_interventions') }}
      </div>

      <recap-component v-else-if="intervention" :intervention="intervention"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.recap {
  padding-top: var(--Spacing-spacing-4);
}

.intervention-selector {
  margin-bottom: var(--Spacing-spacing-2);
}

.intervention-select {
  width: 100%;
}

// Same small uppercase label the Today program tile gives these catalogues, so a title can't be
// mistaken for one more program; a rule above every group after the first separates them.
.catalogue-header {
  @include font-caption-sm;
  color: var(--Neutral-UI-neutral-ui-100);
}

.catalogue-header--after-group {
  border-top: 1px solid var(--Neutral-UI-neutral-ui-30);
}

// Programs nobody categorised in Directus follow the titled groups under the rule alone
.catalogue-header--untitled {
  min-height: 0;
  padding: 0;
}

.no-interventions {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-100);
  text-align: center;
  padding: var(--Spacing-spacing-4);
}

.recap-skeleton-select {
  margin-top: var(--Spacing-spacing-3);
  height: 60px;
  margin-bottom: var(--Spacing-spacing-7);
}

.recap-skeleton-session {
  height: 300px;
  border-radius: var(--card-border-radius);

  @include screen-md {
    height: 500px;
  }
}
</style>
